import {Icon} from '@wandb/components';
import React from 'react';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from 'react-toastify';

import {useViewAction} from '../../../state/views/hooks';
import * as RunSetActions from '../../../state/views/runSet/actions';
import {isRunPinned} from '../../../state/views/runSet/utils';
import {MAX_PINNED_RUNS} from '../../../util/constants';
import {type WBTableRowWithRunSetInfo} from '../../../util/tablecols';
import {useRunSelectorFeatureEnabled} from '../RunSelectorFeaturesContext';
import {
  useCurrentRunSetRef,
  usePinnedAndBaseline,
} from '../RunSelectorRunSetContext';
import {isRunBaseline} from '../util';
import {RowHeaderMenuOption} from './RowHeaderMenuOption';

type GetBaselineLabelOpts = {
  isAnyRunBaseline: boolean;
  isRunBaseline: boolean;
};

function getBaselineLabel({
  isAnyRunBaseline,
  isRunBaseline,
}: GetBaselineLabelOpts): string {
  if (isRunBaseline) {
    return 'Remove baseline';
  } else if (isAnyRunBaseline) {
    return 'Replace baseline';
  } else {
    return 'Set baseline';
  }
}

type GetPinInfoLabelOpts = {
  isRunBaseline: boolean;
  isPinned: boolean;
  pinnedRunCount: number;
};

function getPinInfoLabel({
  isRunBaseline,
  isPinned,
  pinnedRunCount,
}: GetPinInfoLabelOpts): string | null {
  if (pinnedRunCount >= MAX_PINNED_RUNS && !isPinned) {
    return `${MAX_PINNED_RUNS} max`;
  }
  if (!isPinned) {
    return `${pinnedRunCount} of ${MAX_PINNED_RUNS}`;
  }
  return null;
}

type RunPinningAndBaselineMenuOptions = {
  row: WBTableRowWithRunSetInfo;
};

function pinLimitWarning() {
  toast(
    <>
      <strong>Pin limit reached.</strong> Unpin a few runs for greater
      usability.
    </>
  );
}

export function RunPinningAndBaselineMenuOptions({
  row,
}: RunPinningAndBaselineMenuOptions) {
  const isEnabled = useRunSelectorFeatureEnabled();
  const runSetRef = useCurrentRunSetRef();
  const {pinnedRunIds, baselineRunId} = usePinnedAndBaseline();

  const pinned = isRunPinned(pinnedRunIds, row);
  const baseline = isRunBaseline(baselineRunId, row);
  const isAnyRunBaseline = Boolean(baselineRunId);
  const setPinnedRun = useViewAction(runSetRef, RunSetActions.setPinnedRun);
  const setBaselineRun = useViewAction(runSetRef, RunSetActions.setBaselineRun);

  const canPinRun = pinnedRunIds.length < MAX_PINNED_RUNS;
  const remainingPins = MAX_PINNED_RUNS - pinnedRunIds.length;

  const onPinClick = () => {
    window.analytics?.track(pinned ? 'Unpinned row' : 'Pinned row', {
      pinnedRunCount: pinnedRunIds.length,
    });
    setPinnedRun(row, !pinned);

    if (!pinned && remainingPins === 1) {
      pinLimitWarning();
    }
  };
  const onBaselineClick = () => {
    window.analytics?.track(baseline ? 'Removed baseline' : 'Added baseline');
    setBaselineRun(baseline ? null : row);

    if (!baseline && remainingPins === 1) {
      pinLimitWarning();
    }
  };

  const baselineLabel = getBaselineLabel({
    isAnyRunBaseline,
    isRunBaseline: baseline,
  });

  const pinInfoLabel = getPinInfoLabel({
    isRunBaseline: baseline,
    isPinned: pinned,
    pinnedRunCount: pinnedRunIds.length,
  });

  return (
    <>
      {isEnabled('pinning') && !baseline && (
        <RowHeaderMenuOption
          icon={<Icon icon="pin" />}
          infoLabel={pinInfoLabel}
          label={pinned ? 'Unpin run' : 'Pin run'}
          onSelect={onPinClick}
          disabled={!canPinRun && !pinned}
        />
      )}
      {isEnabled('baselining') && (
        <RowHeaderMenuOption
          icon={<Icon icon="bookmark-my-projects" />}
          label={baselineLabel}
          onSelect={onBaselineClick}
          disabled={!canPinRun && !pinned}
        />
      )}
    </>
  );
}
